import { useState } from 'react';

import { postContract, setContractEditStep } from '../../../../resources/contract/actions';
import {
    getContractTravelersInformation,
    getCurrentStep,
} from '../../../../resources/contract/selectors';
import {
    ContractEditStep,
    ContractId,
    Traveler,
    TravelInformation,
} from '../../../../resources/contract/types';
import { useAppDispatch, useAppSelector } from '../../../../shared/redux';
import { useGetContractTravelInformation } from '../../../../resources/contract/hooks';
import { SidePanelViewMode } from '../../SidePanel/types';
import s from '../Contract.css';

import { TravelersStep } from './components/LegalInformation/TravelersStep';
import { TravelInformationStep } from './components/LegalInformation/TravelInformationStep';
import { PaymentScheduleStep } from './components/PaymentSchedule/Step';
import { PricingStep } from './components/QuotePricing/PricingStep';

type Props = {
    contractId: ContractId;
    onSuccess?: () => void;
    viewMode?: SidePanelViewMode;
};

export const ContractEdit = ({ contractId, onSuccess, viewMode }: Props): JSX.Element => {
    const dispatch = useAppDispatch();

    const initialTravelInformation = useGetContractTravelInformation(contractId);

    const initialTravelersInformation = useAppSelector(getContractTravelersInformation);
    const currentStep = useAppSelector(getCurrentStep);

    const [travelers, setTravelers] = useState<Traveler[]>(initialTravelersInformation);
    const [travelInformation, setTravelInformation] = useState<TravelInformation>(
        initialTravelInformation as TravelInformation,
    );

    const saveTravelInformation = (updatedTravelInformation: TravelInformation) => {
        // save updated travel information
        setTravelInformation({
            ...initialTravelInformation,
            ...updatedTravelInformation,
        });

        dispatch(setContractEditStep(ContractEditStep.TRAVELERS));
    };

    const saveTravelers = (updatedTravelers: Traveler[]) => {
        setTravelers(updatedTravelers);

        // call API
        dispatch(postContract(contractId, travelInformation, updatedTravelers));
    };

    const renderCurrentStep = () => {
        switch (currentStep) {
            case ContractEditStep.TRAVEL_INFORMATION:
                return (
                    <TravelInformationStep
                        contractId={contractId}
                        saveTravelInformation={saveTravelInformation}
                        onCancel={onSuccess}
                        viewMode={viewMode}
                    />
                );

            case ContractEditStep.TRAVELERS:
                return (
                    <TravelersStep
                        travelers={travelers}
                        saveTravelers={saveTravelers}
                        viewMode={viewMode}
                    />
                );
            case ContractEditStep.PRICING:
                return (
                    <PricingStep
                        contractId={contractId}
                        numberOfTravelers={travelers.length}
                        viewMode={viewMode}
                    />
                );
            case ContractEditStep.PAYMENT_SCHEDULE:
                return (
                    <PaymentScheduleStep
                        contractId={contractId}
                        onSuccess={onSuccess}
                        viewMode={viewMode}
                    />
                );
            default:
                return null;
        }
    };

    return (
        <div
            className={[s.contractContainer, 'body'].join(' ')}
            {...(viewMode && { 'data-contract-view': viewMode })}
        >
            <div className={s.contractCard}>{renderCurrentStep()}</div>
        </div>
    );
};
